import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, ArrowRight, FileSpreadsheet, Sparkles } from "lucide-react";

/**
 * Public Templates gallery — lead-magnet / SEO landing.
 * Reachable at /templates (no auth). Each template offers XLSX download + "Try in RuleAI" CTA.
 */
export default function Templates() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/templates").then((r) => setItems(r.data.templates));
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-xl tracking-tight" data-testid="brand-logo">
            Rule<span className="text-primary">AI</span>
            <span className="text-muted-foreground">.systems</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" className="rounded-sm">Sign in</Button></Link>
            <Link to="/register">
              <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90">
                Get Started <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <div className="overline text-primary mb-6" data-testid="templates-overline">
              [ FREE · OPERATIONAL EXCELLENCE TEMPLATES ]
            </div>
            <h1 className="font-display font-black text-5xl md:text-6xl leading-[0.95] tracking-tight" data-testid="templates-hero-title">
              10 spreadsheets<br/>
              your operators<br/>
              will actually use.
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Battle-tested Excel templates for Lean, Quality, HR, and Manufacturing —
              with real formulas, conditional formatting, and dropdowns. Free forever.
              One click to open the same template inside RuleAI with AI drafting.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
              <span>◆ Real formulas &amp; validation</span>
              <span>◆ No email required</span>
              <span>◆ Brand-safe editable</span>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-border">
            {items.map((t, i) => (
              <div
                key={t.id}
                data-testid={`template-card-${t.id}`}
                className={`p-8 bg-card border-border flex flex-col
                  ${i % 3 !== 2 ? "lg:border-r" : ""}
                  ${i % 2 !== 1 ? "md:border-r lg:border-r-0" : ""}
                  ${(i + 1) < items.length ? "border-b" : "border-b md:border-b-0"}`}
              >
                <div className="flex items-start justify-between">
                  <FileSpreadsheet className="w-6 h-6 text-primary" />
                  <span className="overline text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="mt-5 font-display font-bold text-xl leading-tight">{t.title}</div>
                <div className="overline text-primary mt-2">{t.category}</div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">
                  {t.description}
                </p>
                <div className="mt-6 flex flex-col gap-2">
                  <a href={`${API}/templates/${t.id}/download`} data-testid={`download-${t.id}`}>
                    <Button className="w-full rounded-sm bg-primary hover:bg-primary/90">
                      <Download className="w-4 h-4 mr-2" /> Download XLSX
                    </Button>
                  </a>
                  <Link to="/register" data-testid={`try-in-app-${t.id}`}>
                    <Button variant="outline" className="w-full rounded-sm">
                      <Sparkles className="w-4 h-4 mr-2" /> Try in RuleAI free
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-zinc-950 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="overline text-accent mb-4">/ NEXT LEVEL</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            Spreadsheets are a start.<br/>
            <span className="text-accent">RuleAI runs your business.</span>
          </h2>
          <p className="text-zinc-400 mt-6 max-w-2xl mx-auto">
            Every template above is already inside RuleAI as a live record with AI drafting,
            approval workflows, audit history, and version control. Start free.
          </p>
          <Link to="/register">
            <Button size="lg" className="rounded-sm bg-accent hover:bg-accent/90 text-white mt-8 h-12 px-8" data-testid="cta-templates-register">
              Start 14-day free trial <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 text-xs font-mono-plex text-muted-foreground uppercase tracking-widest flex items-center justify-between">
          <div>© 2026 RuleAI.Systems</div>
          <Link to="/" className="hover:text-primary">← Home</Link>
        </div>
      </footer>
    </div>
  );
}
